import { X, Loader2, Trash2, AlertCircle, Repeat } from 'lucide-react';
import { useState, useEffect, useMemo, useRef, type ReactNode, type FormEvent } from 'react';

import type { ScheduleEvent } from '../../../services/schedule';
import { fetchEventById, fetchEventSeriesById } from '../../../services/schedule';
import { useScheduleSettings, useClassMutations } from '../../../hooks/schedule/useScheduleSettings';
import { useAuth } from '../../../constants/providers/AuthProvider';

interface CreateEditClassModalProps {
  /** Pass an event to edit, null to create */
  event: ScheduleEvent | null;
  /** Pre-fill the date when creating from a day cell */
  defaultDate?: string;
  onClose: () => void;
  onDeleted?: () => void;
}

interface FormState {
  sport_id: string;
  location_id: string;
  age_bracket_id: string;
  skill_level_id: string;
  coach_ids: string[];
  notes: string;
  event_date: string;
  start_time_local: string; // HH:MM
  end_time_local: string;   // HH:MM
  max_capacity: string;
  is_special_event: boolean;
  // Recurrence
  is_recurring: boolean;
  /** JS getDay() order: 0=Sun, 1=Mon, ..., 6=Sat */
  days_of_week: number[];
  recurrence_unit: 'week' | 'month';
  recurrence_interval: number;
  end_date: string; // optional end date for recurrence
}

const EMPTY_FORM: FormState = {
  sport_id: '',
  location_id: '',
  age_bracket_id: '',
  skill_level_id: '',
  coach_ids: [],
  notes: '',
  event_date: '',
  start_time_local: '09:00',
  end_time_local: '10:00',
  max_capacity: '',
  is_special_event: false,
  is_recurring: false,
  days_of_week: [],
  recurrence_unit: 'week',
  recurrence_interval: 1,
  end_date: '',
};

const DAY_LABELS = ['Su', 'M', 'Tu', 'W', 'Th', 'F', 'Sa'];
const DAY_NAMES = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];

// Convert a local date+time in a specific gym timezone to a UTC ISO string.
// Uses the Intl offset trick so that cross-timezone managers (e.g. owner in EST,
// gym in PST) store the correct UTC time instead of their browser's local offset.
function buildIsoTimestamp(date: string, timeLocal: string, timezone: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const [hours, minutes] = timeLocal.split(':').map(Number);
  // Treat the target local time as a naive UTC instant
  const naiveMs = Date.UTC(year, month - 1, day, hours, minutes, 0);
  // Find what that naive instant looks like when displayed in the target timezone
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false,
    }).formatToParts(new Date(naiveMs)).map(p => [p.type, p.value]),
  );
  const tzDisplayMs = Date.UTC(
    Number(parts.year), Number(parts.month) - 1, Number(parts.day),
    Number(parts.hour) % 24, Number(parts.minute), Number(parts.second),
  );
  // The difference is the UTC offset; apply it to get the correct UTC instant
  return new Date(naiveMs + (naiveMs - tzDisplayMs)).toISOString();
}

// Extract HH:MM from a UTC ISO string using the gym's timezone.
// Falls back to browser timezone when timezone is unknown (legacy data only).
function localTimeFromIso(iso: string | null, timezone: string): string {
  if (!iso) return '09:00';
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      hour: '2-digit', minute: '2-digit',
      hour12: false,
    }).formatToParts(new Date(iso)).map(p => [p.type, p.value]),
  );
  const h = (Number(parts.hour) % 24).toString().padStart(2, '0');
  return `${h}:${parts.minute}`;
}

function dateFromIso(iso: string | null): string {
  if (!iso) return '';
  return iso.split('T')[0];
}

function timeStringToHHMM(t: string | null | undefined): string {
  if (!t) return '09:00';
  // Handles 'HH:MM' or 'HH:MM:SS'
  const parts = t.split(':');
  if (parts.length < 2) return '09:00';
  return `${parts[0].padStart(2, '0')}:${parts[1].padStart(2, '0')}`;
}

// ─── Field wrappers ───────────────────────────────────────────────────────────

function Label({ children }: { children: ReactNode }) {
  return <label className="block text-sm font-medium text-[#EBFFEE] mb-1">{children}</label>;
}

function FieldSelect({
  label,
  value,
  onChange,
  options,
  placeholder,
  required,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { id: string; label: string }[];
  placeholder?: string;
  required?: boolean;
}) {
  return (
    <div>
      <Label>
        {label}
        {required && <span className="text-red-400 ml-0.5">*</span>}
      </Label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
        required={required}
      >
        <option value="">{placeholder ?? '— None —'}</option>
        {options.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── Main modal ───────────────────────────────────────────────────────────────

export function CreateEditClassModal({
  event,
  defaultDate,
  onClose,
  onDeleted,
}: CreateEditClassModalProps) {
  const isEditing = event !== null;
  const { session } = useAuth();
  const userId = session?.user?.id ?? null;

  const { sports, ageBrackets, skillLevels, locations, members, isLoading: loadingSettings } =
    useScheduleSettings();
  const {
    createClass,
    isCreating,
    createError,
    updateClass,
    isUpdating,
    updateError,
    deleteClass,
    isDeleting,
    createEventSeries,
    isCreatingSeries,
    updateEventSeries,
    isUpdatingSeries,
    updateSingleOccurrence,
    isUpdatingOccurrence,
    deleteEventSeries,
    isDeletingSeries,
    deleteFutureEvents,
    isDeletingFuture,
  } = useClassMutations();

  const [form, setForm] = useState<FormState>({ ...EMPTY_FORM, event_date: defaultDate ?? '' });
  const [seriesId, setSeriesId] = useState<string | null>(null);
  // Ref so the edit useEffect can read the latest locations without re-running on location changes
  const locationsRef = useRef(locations);
  locationsRef.current = locations;
  const [editScope, setEditScope] = useState<'single' | 'future' | null>(null);
  const [deleteMode, setDeleteMode] = useState<'none' | 'confirm-single' | 'choose-scope'>('none');
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Populate form when editing
  useEffect(() => {
    if (!isEditing || !event) return;

    const load = async () => {
      try {
        const raw = await fetchEventById(event.event_id);
        if (!raw) return;

        const rawSeriesId: string | null = raw.class_series_id ?? event.class_series_id ?? null;
        setSeriesId(rawSeriesId);

        let recurrenceFields = {
          is_recurring: false,
          days_of_week: [] as number[],
          recurrence_unit: 'week' as 'week' | 'month',
          recurrence_interval: 1,
          end_date: '',
        };

        if (rawSeriesId) {
          try {
            const series = await fetchEventSeriesById(rawSeriesId);
            if (series) {
              const dayNums = (series.repeat_days ?? [])
                .map((name: string) => DAY_NAMES.indexOf(String(name).trim().toLowerCase()))
                .filter((n: number) => n >= 0);
              recurrenceFields = {
                is_recurring: true,
                days_of_week: dayNums,
                recurrence_unit: (series.recurrence_unit === 'month' ? 'month' : 'week'),
                recurrence_interval: series.recurrence_interval ?? 1,
                end_date: series.end_date ?? '',
              };
            }
          } catch {
            // If series fetch fails, treat as non-recurring for this edit
          }
        }

        setForm({
          sport_id: raw.sport_id ?? '',
          location_id: raw.location_id ?? '',
          age_bracket_id: raw.age_bracket_id ?? '',
          skill_level_id: raw.skill_level_id ?? '',
          coach_ids: raw.coach_ids ?? [],
          notes: raw.notes ?? '',
          event_date: raw.event_date ?? dateFromIso(raw.start_time),
          start_time_local: raw.start_time_local
            ? timeStringToHHMM(raw.start_time_local)
            : localTimeFromIso(raw.start_time, locationsRef.current.find(l => l.id === raw.location_id)?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone),
          end_time_local: raw.end_time_local
            ? timeStringToHHMM(raw.end_time_local)
            : localTimeFromIso(raw.end_time, locationsRef.current.find(l => l.id === raw.location_id)?.timezone ?? Intl.DateTimeFormat().resolvedOptions().timeZone),
          max_capacity: raw.max_capacity?.toString() ?? '',
          is_special_event: raw.is_special_event ?? false,
          ...recurrenceFields,
        });
      } catch {
        // silently ignore — form stays empty
      }
    };
    load();
  }, [isEditing, event]);

  const set = <K extends keyof FormState>(key: K, value: FormState[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleCoach = (id: string) =>
    set(
      'coach_ids',
      form.coach_ids.includes(id)
        ? form.coach_ids.filter((c) => c !== id)
        : [...form.coach_ids, id]
    );

  const toggleDay = (idx: number) => {
    const next = form.days_of_week.includes(idx)
      ? form.days_of_week.filter((d) => d !== idx)
      : [...form.days_of_week, idx].sort((a, b) => a - b);
    set('days_of_week', next);
  };

  const selectedLocation = useMemo(
    () => locations.find((l) => l.id === form.location_id) ?? null,
    [locations, form.location_id]
  );

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitError(null);

    if (!form.sport_id) return setSubmitError('Please select a sport / class type.');
    if (!form.event_date) return setSubmitError('Please select a date.');
    if (!form.location_id) return setSubmitError('Please select a location.');

    if (form.end_time_local === '00:00') {
      return setSubmitError('End time cannot be 12:00 AM (00:00).');
    }

    const [sh, sm] = form.start_time_local.split(':').map(Number);
    const [eh, em] = form.end_time_local.split(':').map(Number);
    if (eh * 60 + em <= sh * 60 + sm) {
      return setSubmitError('End time must be after start time.');
    }

    if (form.is_recurring && form.days_of_week.length === 0) {
      return setSubmitError('Pick at least one day of week for recurring classes.');
    }

    const timezone =
      (selectedLocation as { timezone?: string } | null)?.timezone ||
      Intl.DateTimeFormat().resolvedOptions().timeZone;
    const start_time = buildIsoTimestamp(form.event_date, form.start_time_local, timezone);
    const end_time = buildIsoTimestamp(form.event_date, form.end_time_local, timezone);
    const start_time_local = `${form.start_time_local}:00`;
    const end_time_local = `${form.end_time_local}:00`;
    const maxCapacity = form.max_capacity ? parseInt(form.max_capacity, 10) : null;
    const notes = form.notes.trim() === '' ? null : form.notes;
    const repeat_days = form.days_of_week.map((i) => DAY_NAMES[i]);

    try {
      if (isEditing && event) {
        // ── Editing ─────────────────────────────────────────────────
        if (seriesId) {
          // Recurring event — scope matters
          const scope = editScope ?? 'future';
          if (scope === 'single') {
            await updateSingleOccurrence({
              eventId: event.event_id,
              company_id: null,
              coach_ids: form.coach_ids,
              sport_id: form.sport_id,
              skill_level_id: form.skill_level_id || null,
              age_bracket_id: form.age_bracket_id || null,
              location_id: form.location_id || null,
              max_capacity: maxCapacity,
              notes,
            });
          } else {
            await updateEventSeries({
              seriesId,
              input: {
                sport_id: form.sport_id,
                location_id: form.location_id,
                age_bracket_id: form.age_bracket_id || null,
                skill_level_id: form.skill_level_id || null,
                coach_ids: form.coach_ids,
                notes,
                max_capacity: maxCapacity,
                start_date: form.event_date,
                end_date: form.end_date || null,
                start_time: start_time_local,
                end_time: end_time_local,
                recurrence_unit: form.recurrence_unit,
                recurrence_interval: form.recurrence_interval,
                repeat_days: form.is_recurring ? repeat_days : null,
                timezone,
              },
            });
          }
        } else {
          // Single non-recurring event
          await updateClass({
            eventId: event.event_id,
            input: {
              sport_id: form.sport_id,
              location_id: form.location_id || null,
              age_bracket_id: form.age_bracket_id || null,
              skill_level_id: form.skill_level_id || null,
              coach_ids: form.coach_ids,
              notes,
              start_time,
              end_time,
              event_date: form.event_date,
              start_time_local,
              end_time_local,
              max_capacity: maxCapacity,
              is_special_event: form.is_special_event,
            },
          });
        }
      } else {
        // ── Creating ────────────────────────────────────────────────
        if (form.is_recurring) {
          await createEventSeries({
            sport_id: form.sport_id,
            location_id: form.location_id,
            age_bracket_id: form.age_bracket_id || null,
            skill_level_id: form.skill_level_id || null,
            coach_ids: form.coach_ids,
            notes,
            max_capacity: maxCapacity,
            start_date: form.event_date,
            end_date: form.end_date || null,
            start_time: start_time_local,
            end_time: end_time_local,
            recurrence_unit: form.recurrence_unit,
            recurrence_interval: form.recurrence_interval,
            repeat_days,
            timezone,
            is_special_event: form.is_special_event,
            created_by: userId,
          });
        } else {
          await createClass({
            sport_id: form.sport_id,
            location_id: form.location_id || null,
            age_bracket_id: form.age_bracket_id || null,
            skill_level_id: form.skill_level_id || null,
            coach_ids: form.coach_ids,
            notes,
            start_time,
            end_time,
            event_date: form.event_date,
            start_time_local,
            end_time_local,
            max_capacity: maxCapacity,
            is_special_event: form.is_special_event,
            created_by: userId,
          });
        }
      }
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Something went wrong.');
    }
  };

  const runDelete = async (mode: 'single' | 'series-all' | 'series-future') => {
    if (!event) return;
    try {
      if (mode === 'single') {
        await deleteClass({ eventId: event.event_id });
      } else if (mode === 'series-all' && seriesId) {
        await deleteEventSeries({ seriesId });
      } else if (mode === 'series-future' && seriesId) {
        await deleteFutureEvents({ seriesId });
      }
      onDeleted?.();
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Delete failed.');
      setDeleteMode('none');
    }
  };

  const openDelete = () => {
    if (seriesId) setDeleteMode('choose-scope');
    else setDeleteMode('confirm-single');
  };

  const isBusy =
    isCreating ||
    isUpdating ||
    isDeleting ||
    isCreatingSeries ||
    isUpdatingSeries ||
    isUpdatingOccurrence ||
    isDeletingSeries ||
    isDeletingFuture;
  const apiError = submitError ?? (createError?.message ?? updateError?.message ?? null);

  // Coaches = members with staff roles
  const staffMembers = members.filter((m) =>
    ['owner', 'admin', 'coach', 'instructor'].includes(m.role)
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60"
      onClick={(e) => e.target === e.currentTarget && onClose()}
    >
      <div className="bg-[#1A1717] border border-[#504A4A] rounded-2xl w-full max-w-lg max-h-[92vh] flex flex-col shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[#363232] shrink-0">
          <h2 className="text-[#EBFFEE] font-bold text-lg">
            {isEditing ? 'Edit Class' : 'Create Class'}
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 text-[#A9A3A3] hover:text-[#EBFFEE] hover:bg-[#363232] rounded-lg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scope chooser for editing a recurring class */}
        {isEditing && seriesId && editScope === null && (
          <div className="px-5 py-3 border-b border-[#363232] bg-[#9B1421]/10 shrink-0">
            <p className="text-[#EBFFEE] text-sm font-medium mb-2">
              This is a recurring class. Apply changes to:
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setEditScope('single')}
                className="flex-1 text-xs bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] py-2 rounded-lg transition-colors"
              >
                This class only
              </button>
              <button
                type="button"
                onClick={() => setEditScope('future')}
                className="flex-1 text-xs bg-[#9B1421] hover:bg-[#7A0F1A] text-white py-2 rounded-lg transition-colors"
              >
                All future classes
              </button>
            </div>
          </div>
        )}

        {/* Body */}
        <form onSubmit={handleSubmit} id="create-edit-form" className="flex-1 overflow-y-auto p-5 space-y-4">
          {loadingSettings && (
            <div className="flex justify-center py-6">
              <Loader2 size={24} className="animate-spin text-[#A9A3A3]" />
            </div>
          )}

          {!loadingSettings && (
            <>
              {/* Sport */}
              <FieldSelect
                label="Sport / Class Type"
                value={form.sport_id}
                onChange={(v) => set('sport_id', v)}
                options={sports.map((s) => ({ id: s.id, label: s.sport_name }))}
                placeholder="— Select sport —"
                required
              />

              {/* Date */}
              <div>
                <Label>
                  {form.is_recurring ? 'Start Date' : 'Date'}
                  <span className="text-red-400 ml-0.5">*</span>
                </Label>
                <input
                  type="date"
                  value={form.event_date}
                  onChange={(e) => set('event_date', e.target.value)}
                  className="w-full bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                  required
                />
              </div>

              {/* Time */}
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start Time</Label>
                  <input
                    type="time"
                    value={form.start_time_local}
                    onChange={(e) => set('start_time_local', e.target.value)}
                    className="w-full bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                  />
                </div>
                <div>
                  <Label>End Time</Label>
                  <input
                    type="time"
                    value={form.end_time_local}
                    onChange={(e) => set('end_time_local', e.target.value)}
                    className="w-full bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                  />
                </div>
              </div>

              {/* Recurring toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <div
                  onClick={() => {
                    const next = !form.is_recurring;
                    // Seed with selected event_date's weekday when enabling
                    let days = form.days_of_week;
                    if (next && days.length === 0 && form.event_date) {
                      const d = new Date(`${form.event_date}T00:00:00`).getDay();
                      days = [d];
                    }
                    setForm((prev) => ({ ...prev, is_recurring: next, days_of_week: days }));
                  }}
                  className={`w-10 h-5 rounded-full transition-colors relative ${
                    form.is_recurring ? 'bg-[#9B1421]' : 'bg-[#504A4A]'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      form.is_recurring ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </div>
                <span className="text-sm text-[#EBFFEE] flex items-center gap-1.5">
                  <Repeat size={14} /> Recurring class
                </span>
              </label>

              {form.is_recurring && (
                <div className="bg-[#111010] border border-[#504A4A] rounded-lg p-3 space-y-3">
                  {/* Day-of-week chips */}
                  <div>
                    <Label>Days of Week</Label>
                    <div className="flex gap-1.5 justify-between">
                      {DAY_LABELS.map((lbl, i) => {
                        const selected = form.days_of_week.includes(i);
                        return (
                          <button
                            key={i}
                            type="button"
                            onClick={() => toggleDay(i)}
                            className={`flex-1 h-10 rounded-full text-sm font-semibold transition-colors border ${
                              selected
                                ? 'bg-[#9B1421] border-[#9B1421] text-white'
                                : 'bg-[#2A2626] border-[#504A4A] text-[#A9A3A3] hover:border-[#6A6464]'
                            }`}
                          >
                            {lbl}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Interval + unit */}
                  <div>
                    <Label>Occurs Every</Label>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min={1}
                        max={52}
                        value={form.recurrence_interval}
                        onChange={(e) =>
                          set('recurrence_interval', Math.max(1, parseInt(e.target.value || '1', 10)))
                        }
                        className="w-20 bg-[#1A1717] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                      />
                      <select
                        value={form.recurrence_unit}
                        onChange={(e) =>
                          set('recurrence_unit', e.target.value === 'month' ? 'month' : 'week')
                        }
                        className="flex-1 bg-[#1A1717] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                      >
                        <option value="week">Week(s)</option>
                        <option value="month">Month(s)</option>
                      </select>
                    </div>
                  </div>

                  {/* Optional end date */}
                  <div>
                    <Label>Ends On (optional)</Label>
                    <input
                      type="date"
                      value={form.end_date}
                      onChange={(e) => set('end_date', e.target.value)}
                      className="w-full bg-[#1A1717] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] focus:outline-none focus:border-[#9B1421]"
                    />
                  </div>
                </div>
              )}

              {/* Location */}
              <FieldSelect
                label="Location"
                value={form.location_id}
                onChange={(v) => set('location_id', v)}
                options={locations.map((l) => ({ id: l.id, label: l.name }))}
                placeholder="— Select location —"
                required
              />

              {/* Capacity */}
              <div>
                <Label>Max Capacity</Label>
                <input
                  type="number"
                  min="1"
                  max="999"
                  value={form.max_capacity}
                  onChange={(e) => set('max_capacity', e.target.value)}
                  placeholder="No limit"
                  className="w-full bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] placeholder-[#504A4A] focus:outline-none focus:border-[#9B1421]"
                />
              </div>

              {/* Age bracket */}
              {ageBrackets.length > 0 && (
                <FieldSelect
                  label="Age Bracket"
                  value={form.age_bracket_id}
                  onChange={(v) => set('age_bracket_id', v)}
                  options={ageBrackets.map((a) => ({ id: a.id, label: a.title }))}
                />
              )}

              {/* Skill level */}
              {skillLevels.length > 0 && (
                <FieldSelect
                  label="Skill Level"
                  value={form.skill_level_id}
                  onChange={(v) => set('skill_level_id', v)}
                  options={skillLevels.map((s) => ({ id: s.id, label: s.title }))}
                />
              )}

              {/* Coaches */}
              {staffMembers.length > 0 && (
                <div>
                  <Label>Coaches / Instructors</Label>
                  <div className="flex flex-wrap gap-2 mt-1">
                    {staffMembers.map((m) => {
                      const selected = form.coach_ids.includes(m.id);
                      return (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => toggleCoach(m.id)}
                          className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
                            selected
                              ? 'bg-[#9B1421]/20 border-[#9B1421] text-[#EBFFEE]'
                              : 'bg-[#2A2626] border-[#504A4A] text-[#A9A3A3] hover:border-[#6A6464]'
                          }`}
                        >
                          {m.first_name} {m.last_name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Notes */}
              <div>
                <Label>Notes / Description</Label>
                <textarea
                  value={form.notes}
                  onChange={(e) => set('notes', e.target.value)}
                  rows={3}
                  placeholder="Optional class notes..."
                  className="w-full bg-[#111010] border border-[#504A4A] rounded-lg px-3 py-2 text-sm text-[#EBFFEE] placeholder-[#504A4A] focus:outline-none focus:border-[#9B1421] resize-none"
                />
              </div>

              {/* Special event toggle */}
              <label className="flex items-center gap-3 cursor-pointer">
                <div
                  onClick={() => set('is_special_event', !form.is_special_event)}
                  className={`w-10 h-5 rounded-full transition-colors relative ${
                    form.is_special_event ? 'bg-[#9B1421]' : 'bg-[#504A4A]'
                  }`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      form.is_special_event ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </div>
                <span className="text-sm text-[#A9A3A3]">Special event</span>
              </label>
            </>
          )}

          {/* Error */}
          {apiError && (
            <div className="flex items-start gap-2 bg-red-900/20 border border-red-600/40 rounded-lg px-3 py-2">
              <AlertCircle size={14} className="text-red-400 shrink-0 mt-0.5" />
              <p className="text-red-300 text-sm">{apiError}</p>
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-[#363232] shrink-0 space-y-2">
          {/* Delete — single event */}
          {isEditing && deleteMode === 'confirm-single' && (
            <div className="bg-red-900/20 border border-red-600/40 rounded-lg p-3 mb-2">
              <p className="text-red-300 text-sm font-medium mb-2">
                Delete this class? All enrollments will be removed.
              </p>
              <div className="flex gap-2">
                <button
                  onClick={() => runDelete('single')}
                  disabled={isBusy}
                  className="flex-1 flex items-center justify-center gap-1.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold py-2 rounded-lg transition-colors disabled:opacity-50"
                >
                  {isDeleting ? <Loader2 size={14} className="animate-spin" /> : <Trash2 size={14} />}
                  Confirm Delete
                </button>
                <button
                  onClick={() => setDeleteMode('none')}
                  className="flex-1 text-sm text-[#A9A3A3] hover:text-[#EBFFEE] bg-[#363232] py-2 rounded-lg transition-colors"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Delete — recurring series scope */}
          {isEditing && deleteMode === 'choose-scope' && (
            <div className="bg-red-900/20 border border-red-600/40 rounded-lg p-3 mb-2 space-y-2">
              <p className="text-red-300 text-sm font-medium">
                Delete recurring class. Choose scope:
              </p>
              <button
                onClick={() => runDelete('single')}
                disabled={isBusy}
                className="w-full text-sm bg-[#363232] hover:bg-[#504A4A] text-[#EBFFEE] py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                This class only
              </button>
              <button
                onClick={() => runDelete('series-future')}
                disabled={isBusy}
                className="w-full text-sm bg-red-600 hover:bg-red-700 text-white py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                All future classes
              </button>
              <button
                onClick={() => runDelete('series-all')}
                disabled={isBusy}
                className="w-full text-sm bg-red-800 hover:bg-red-900 text-white py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                Entire series (past + future)
              </button>
              <button
                onClick={() => setDeleteMode('none')}
                className="w-full text-sm text-[#A9A3A3] hover:text-[#EBFFEE] bg-[#363232] py-2 rounded-lg transition-colors"
              >
                Cancel
              </button>
            </div>
          )}

          <div className="flex gap-2">
            {isEditing && deleteMode === 'none' && (
              <button
                type="button"
                onClick={openDelete}
                className="p-2.5 text-[#A9A3A3] hover:text-red-400 hover:bg-red-900/20 rounded-lg transition-colors"
              >
                <Trash2 size={16} />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="flex-1 text-sm text-[#A9A3A3] hover:text-[#EBFFEE] bg-[#363232] hover:bg-[#504A4A] py-2.5 rounded-xl transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              form="create-edit-form"
              disabled={isBusy || loadingSettings}
              className="flex-1 flex items-center justify-center gap-2 bg-[#9B1421] hover:bg-[#7A0F1A] text-white font-semibold py-2.5 rounded-xl transition-colors disabled:opacity-50"
            >
              {isBusy ? (
                <Loader2 size={16} className="animate-spin" />
              ) : isEditing ? (
                'Save Changes'
              ) : (
                'Create Class'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
